"use client";

import React, { useState } from "react";
import { Plus } from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
const faqItems = [{
  id: "q1",
  index: "01",
  question: "How much space do you need for a mechanical bull?",
  answer: "Space requirements vary by machine and inflatable safety arena. Check the complete operating dimensions for the exact equipment rather than using one universal footprint."
}, {
  id: "q2",
  index: "02",
  question: "What power does a mechanical bull need?",
  answer: "Power requirements vary by model and configuration. Confirm the electrical requirements for the exact bull, controller and blower before installation."
}, {
  id: "q3",
  index: "03",
  question: "How do you set up a mechanical bull?",
  answer: "Setup generally involves positioning the bull and motion base, controller and safety arena, connecting the required equipment and following the product-specific instructions before operation. The exact process varies between machines."
}, {
  id: "q4",
  index: "04",
  question: "Does a mechanical bull need an inflatable?",
  answer: "Many mechanical bull systems use an inflatable safety arena, but package designs vary. Check what the individual machine requires and what comes with the listing."
}, {
  id: "q5",
  index: "05",
  question: "Can a mechanical bull be used indoors?",
  answer: "Whether a machine is suitable for a particular indoor location depends on the equipment requirements, available space, access, power and the instructions for that specific machine."
}, {
  id: "q6",
  index: "06",
  question: "Can a mechanical bull be moved between events?",
  answer: "Some buyers use mechanical bulls in mobile operations. Before buying for this purpose, compare packed dimensions, transport requirements, loading access and repeated setup considerations for the exact equipment."
}, {
  id: "q7",
  index: "07",
  question: "What should I check before operating a mechanical bull?",
  answer: "Review the visible condition of the machine, controller, inflatable, blower, cables and relevant controls, then follow the pre-use procedure provided for the exact equipment."
}, {
  id: "q8",
  index: "08",
  question: "How often does a mechanical bull need maintenance?",
  answer: "Maintenance intervals vary by machine and component. Follow the schedule and procedures supplied by the manufacturer or documented for the exact equipment."
}];
export default function Faq() {
  const [openId, setOpenId] = useState<string | null>("q1");
  const prefersReducedMotion = useReducedMotion();
  const toggleItem = (id: string) => {
    setOpenId(current => current === id ? null : id);
  };
  return <section id="mechanicalbullfaq" data-nav="light" className="w-full border-y border-border bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="border border-border bg-card p-6 md:p-10 shadow-xs">
          <div className="flex items-center gap-3 border-b border-border pb-4">
            <span className="inline-block size-2 bg-primary" />
            <span className="font-mono text-xs font-semibold tracking-widest uppercase text-muted-foreground">
              TECHNICAL SPECIFICATION & PROTOCOLS
            </span>
          </div>

          <div className="mt-6 flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div>
              <h2 className="font-serif text-3xl font-bold uppercase tracking-tight text-foreground sm:text-4xl md:text-5xl">
                Mechanical Bull Setup & Safety FAQ
              </h2>
            </div>
            <div className="font-mono text-xs text-muted-foreground uppercase">
              Ref: DOC-804 // Rev 03
            </div>
          </div>
        </div>

        {/* Industrial Accordion Grid */}
        <div className="mt-8 border border-border bg-card shadow-xs">
          <div className="divide-y divide-border">
            {faqItems.map((item, i) => {
            const isOpen = openId === item.id;
            return <div key={item.id} data-index={i} className="group transition-colors duration-150 hover:bg-muted/30">
                  <button type="button" onClick={() => toggleItem(item.id)} aria-expanded={isOpen} aria-controls={`faq-answer-${item.id}`} className="flex w-full items-start justify-between gap-4 p-5 text-left md:p-6 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1">
                    <div className="flex items-start gap-4 md:gap-6">
                      <span className="font-mono text-xs font-bold text-primary pt-1 shrink-0">
                        [{item.index}]
                      </span>
                      <span className="font-serif text-lg font-semibold uppercase tracking-wide text-foreground sm:text-xl">
                        {item.question}
                      </span>
                    </div>

                    <div className="ml-4 flex size-8 shrink-0 items-center justify-center border border-border bg-background transition-colors duration-150 group-hover:border-foreground/30">
                      <motion.div animate={{
                    rotate: isOpen ? 45 : 0
                  }} transition={prefersReducedMotion ? {
                    duration: 0
                  } : {
                    duration: 0.22,
                    ease: [0.2, 0, 0, 1]
                  }}>
                        <Plus className="size-4 text-foreground" />
                      </motion.div>
                    </div>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && <motion.div id={`faq-answer-${item.id}`} initial={prefersReducedMotion ? {
                  opacity: 1,
                  height: "auto"
                } : {
                  opacity: 0,
                  height: 0
                }} animate={prefersReducedMotion ? {
                  opacity: 1,
                  height: "auto"
                } : {
                  opacity: 1,
                  height: "auto"
                }} exit={prefersReducedMotion ? {
                  opacity: 0,
                  height: 0
                } : {
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.22,
                  ease: [0.2, 0, 0, 1]
                }} className="overflow-hidden">
                        <div className="border-t border-border/60 bg-muted/20 px-6 py-5 pl-14 sm:pl-16 md:px-8 md:py-6 md:pl-20">
                          <p className="font-sans text-sm leading-relaxed text-muted-foreground md:text-base">
                            {item.answer}
                          </p>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>;
          })}
          </div>
        </div>

        {/* Industrial Footer Note */}
        <div className="mt-4 flex flex-col justify-between gap-2 border-t border-dashed border-border pt-4 font-mono text-xs text-muted-foreground sm:flex-row">
          <span>SPECIFICATION STATUS: VERIFIED COMMERCIAL EQUIPMENT</span>
          <span>COMPLIANCE: CHECK SPECIFIC MODEL MANUAL</span>
        </div>
      </div>
    </section>;
}